import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import Checklist from "@/components/sections/Checklist";
import CTABlock from "@/components/sections/CTABlock";
import ProcessSteps from "@/components/sections/ProcessSteps";
import FeatureGrid from "@/components/sections/FeatureGrid";
import heroImage from "@/assets/property-prep-hero.jpg";

const PropertyPreparationLanding = () => {
  useEffect(() => {
    document.title = "Property Preparation Services | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Increase your home's value before you sell. Hadassah Homes provides strategic property preparation services including cleaning, repairs, and painting.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/property-preparation";
  }, []);

  return (
  <SiteLayout>
    {/* SECTION 1 — HERO */}
    <PageHero
      eyebrow="Property Preparation"
      title="Property Preparation Services"
      subtitle="The Smartest Way to Increase Your Home's Value Before You Sell"
      backgroundImage={heroImage}
    />

    {/* SECTION 2 — INTRO */}
    <ContentSection>
      <div className="max-w-3xl mx-auto space-y-6 text-muted-foreground text-base md:text-lg leading-relaxed">
        <p>Most homeowners focus on selling their home.</p>
        <p>The smarter approach?</p>
        <p>Prepare it to perform before it ever hits the market.</p>
        <p>At Hadassah Homes, we help you make the right improvements—so your home shows better, attracts stronger buyers, and positions you for a better outcome.</p>
        <p>Because the difference between a good sale and a great one often comes down to what's done before the listing goes live.</p>
        <p className="text-foreground font-headline text-xl md:text-2xl pt-4">Find out how to increase your home's value</p>
        <Link
          to="/consultation"
          className="inline-block font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors mt-2"
        >
          Book a Consultation →
        </Link>
      </div>
    </ContentSection>

    {/* SECTION 3 — WHY PREPARATION CHANGES EVERYTHING */}
    <ContentSection variant="dark">
      <SectionHeader title="Why Preparation Changes Everything" />
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed">
          <p>Buyers don't just evaluate a home—they react to it.</p>
          <p>Within seconds of walking in, they're deciding:</p>
        </div>
        <Checklist
          items={[
            "Does this feel move-in ready?",
            "Does this home feel well-maintained?",
            "Is this worth the price?",
          ]}
        />
        <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed pt-4">
          <p>Even small details—scuffed walls, outdated colors, minor repairs—can quietly reduce perceived value.</p>
          <p>The right preparation removes those doubts and replaces them with confidence.</p>
        </div>
      </div>
    </ContentSection>

    {/* SECTION 4 — THE HIDDEN COST OF SKIPPING PREPARATION */}
    <ContentSection>
      <SectionHeader title="The Hidden Cost of Skipping Preparation" />
      <div className="max-w-3xl mx-auto space-y-8">
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Homes that aren't properly prepared often:</p>
        <Checklist
          items={[
            "Sit longer on the market",
            "Receive weaker offers",
            "Require price reductions",
            "Attract less serious buyers",
          ]}
        />
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed pt-4">Not because they aren't good homes—but because they weren't presented at their full potential.</p>
      </div>
    </ContentSection>

    {/* SECTION 5 — A STRATEGIC, NOT OVERWHELMING APPROACH */}
    <ContentSection variant="dark">
      <SectionHeader title="A Strategic, Not Overwhelming Approach" />
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed">
          <p>Preparation doesn't mean renovating everything.</p>
          <p>It means focusing on the few changes that create the biggest impact.</p>
          <p>We help you:</p>
        </div>
        <Checklist
          items={[
            "Identify high-return improvements",
            "Avoid unnecessary upgrades",
            "Prioritize what actually influences buyers",
            "Create a clean, cohesive, move-in-ready feel",
          ]}
        />
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed pt-4">The goal is simple: maximize results without overspending.</p>
      </div>
    </ContentSection>

    {/* SECTION 6 — OUR PROPERTY PREPARATION SERVICES */}
    <ContentSection>
      <SectionHeader title="Our Property Preparation Services" />
      <div className="max-w-3xl mx-auto space-y-6 text-muted-foreground text-base md:text-lg leading-relaxed mb-14">
        <p>We provide hands-on services to help bring your home to market-ready condition—without the stress of coordinating everything yourself.</p>
      </div>

      {/* Service 1 — Cleaning */}
      <div className="max-w-3xl mx-auto space-y-8 mb-20">
        <h3 className="font-headline text-2xl md:text-3xl text-foreground">
          <Link to="/cleaning" className="hover:text-accent transition-colors">Cleaning</Link>
        </h3>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">A professionally cleaned home immediately feels more valuable.</p>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We focus on:</p>
        <Checklist
          items={[
            "Deep cleaning throughout the home",
            "Removing buildup, odors, and visual clutter",
            "Creating a fresh, bright, and well-maintained impression",
          ]}
        />
        <p className="text-foreground font-headline text-lg md:text-xl pt-4">A clean home sets the foundation for everything else.</p>
      </div>

      {/* Service 2 — Carpentry & Repairs */}
      <div className="max-w-3xl mx-auto space-y-8 mb-20">
        <h3 className="font-headline text-2xl md:text-3xl text-foreground">
          <Link to="/carpentry" className="hover:text-accent transition-colors">Carpentry & Repairs</Link>
        </h3>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Small issues can create big concerns for buyers.</p>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We address:</p>
        <Checklist
          items={[
            "Minor repairs and visible wear",
            "Doors, trim, fixtures, and functional details",
            "Issues that may raise red flags during showings or inspections",
          ]}
        />
        <p className="text-foreground font-headline text-lg md:text-xl pt-4">Removing these distractions increases buyer confidence.</p>
      </div>

      {/* Service 3 — Painting */}
      <div className="max-w-3xl mx-auto space-y-8">
        <h3 className="font-headline text-2xl md:text-3xl text-foreground">
          <Link to="/painting" className="hover:text-accent transition-colors">Painting</Link>
        </h3>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Fresh paint is one of the simplest—and most powerful—ways to transform a home.</p>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">We help:</p>
        <Checklist
          items={[
            "Replace outdated or bold colors",
            "Brighten and modernize spaces",
            "Create a neutral, widely appealing look",
          ]}
        />
        <p className="text-foreground font-headline text-lg md:text-xl pt-4">The right paint can completely change how a home is perceived.</p>
      </div>
    </ContentSection>

    {/* SECTION 7 — BEFORE & AFTER */}
    <ContentSection variant="dark">
      <SectionHeader title="Before & After: The Difference Preparation Makes" />
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          <div className="space-y-6">
            <h3 className="font-headline text-2xl text-foreground">Before Preparation</h3>
            <Checklist
              items={[
                "Dated or inconsistent finishes",
                "Visible wear and minor issues",
                "Lower perceived value",
                "Buyer hesitation",
              ]}
            />
          </div>
          <div className="space-y-6">
            <h3 className="font-headline text-2xl text-foreground">After Preparation</h3>
            <Checklist
              items={[
                "Clean, modern, and cohesive presentation",
                "Strong first impression",
                "Increased buyer confidence",
                "Higher perceived value and stronger interest",
              ]}
            />
          </div>
        </div>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed pt-4">This isn't about perfection—it's about positioning.</p>
      </div>
    </ContentSection>

    {/* SECTION 8 — REAL IMPACT */}
    <ContentSection>
      <SectionHeader title="Real Impact: Small Changes, Stronger Results" />
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed">
          <p>In many cases, relatively small updates can lead to significantly better outcomes.</p>
          <p>For example:</p>
        </div>
        <Checklist
          items={[
            "A fresh coat of paint can make spaces feel newer and more valuable",
            "Minor repairs can eliminate objections before they arise",
            "A deep clean can completely shift how buyers experience the home",
          ]}
        />
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed pt-4">These improvements don't just make your home look better—they make it sell better.</p>
      </div>
    </ContentSection>

    {/* SECTION 9 — DONE-FOR-YOU PROCESS */}
    <ContentSection variant="dark">
      <SectionHeader title="A Seamless, Done-for-You Process" />
      <div className="max-w-3xl mx-auto space-y-8 mb-14">
        <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed">
          <p>We don't just tell you what to do—we help you execute it.</p>
          <p>Our process:</p>
        </div>
      </div>
      <ProcessSteps
        layout="horizontal"
        steps={[
          { title: "Evaluate", description: "Walk through your home and identify high-impact opportunities" },
          { title: "Plan", description: "Create a clear, prioritized preparation plan" },
          { title: "Execute", description: "Coordinate cleaning, repairs, and updates" },
          { title: "Launch", description: "Ensure your home is ready before it hits the market" },
        ]}
      />
      <div className="max-w-3xl mx-auto mt-14">
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">You get clarity, support, and a streamlined experience from start to finish.</p>
      </div>
    </ContentSection>

    {/* SECTION 10 — WHY SELLERS CHOOSE HADASSAH HOMES */}
    <ContentSection>
      <SectionHeader title="Why Sellers Choose Hadassah Homes" />
      <div className="max-w-3xl mx-auto space-y-8">
        <Checklist
          items={[
            "A clear strategy before listing",
            "Access to trusted, coordinated services",
            "Focus on value—not unnecessary work",
            "A smoother, more confident selling experience",
          ]}
        />
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed pt-4">We help you prepare with purpose—not guesswork.</p>
      </div>
    </ContentSection>

    {/* SECTION 11 — FINAL CTA */}
    <CTABlock
      variant="dark"
      title="Let's Prepare Your Home the Right Way"
      description="The best time to increase your home's value is before buyers ever see it. We'll help you understand exactly what to do—and what not to do—so you can move forward with confidence and maximize your result."
      primaryCta={{ text: "Book a Consultation →", href: "/consultation" }}
    />
  </SiteLayout>
  );
};

export default PropertyPreparationLanding;
